/**
 * Clara360 Intelligent Exposé & Pricing Engine
 * Automatische Exposé-Generierung mit Mietspiegel-Integration
 */

import { masterSupabase } from '../lib/supabase-master.js';

class ExposePricingEngine {
  constructor() {
    this.marketData = new Map();
    this.equipmentFactors = this.initializeEquipmentFactors();
    this.locationMultipliers = this.initializeLocationMultipliers();
  }

  /**
   * Ausstattungsfaktoren für Mietpreisberechnung
   */
  initializeEquipmentFactors() {
    return {
      // Küche
      'einbaukueche': { factor: 1.08, description: 'Einbauküche' },
      'moderne_kueche': { factor: 1.05, description: 'Moderne Küche' },
      
      // Bad
      'badewanne': { factor: 1.03, description: 'Badewanne' },
      'dusche': { factor: 1.02, description: 'Dusche' },
      'gaeste_wc': { factor: 1.04, description: 'Gäste-WC' },
      
      // Böden
      'parkett': { factor: 1.06, description: 'Parkettboden' },
      'laminat': { factor: 1.02, description: 'Laminatboden' },
      'fliesen': { factor: 1.01, description: 'Fliesenboden' },
      
      // Heizung
      'fussbodenheizung': { factor: 1.07, description: 'Fußbodenheizung' },
      'zentralheizung': { factor: 1.02, description: 'Zentralheizung' },
      
      // Außenbereiche
      'balkon': { factor: 1.05, description: 'Balkon' },
      'terrasse': { factor: 1.08, description: 'Terrasse' },
      'garten': { factor: 1.12, description: 'Garten' },
      
      // Sonstiges
      'aufzug': { factor: 1.04, description: 'Aufzug' },
      'keller': { factor: 1.03, description: 'Keller' },
      'stellplatz': { factor: 1.06, description: 'Stellplatz' },
      'garage': { factor: 1.10, description: 'Garage' },
      'klimaanlage': { factor: 1.08, description: 'Klimaanlage' }
    };
  }

  /**
   * Standort-Multiplikatoren basierend auf Lage
   */
  initializeLocationMultipliers() {
    return {
      // Beispiel-Städte (in Produktion aus Mietspiegel-API)
      'München': { base: 1.4, premium: 1.8 },
      'Frankfurt': { base: 1.3, premium: 1.6 },
      'Hamburg': { base: 1.2, premium: 1.5 },
      'Berlin': { base: 1.1, premium: 1.4 },
      'Köln': { base: 1.1, premium: 1.3 },
      'Stuttgart': { base: 1.2, premium: 1.5 },
      'Düsseldorf': { base: 1.2, premium: 1.5 },
      'Leipzig': { base: 0.8, premium: 1.0 },
      'Dresden': { base: 0.8, premium: 1.0 },
      'default': { base: 1.0, premium: 1.2 }
    };
  }

  /**
   * Hauptfunktion: Vollständiges Exposé mit Pricing generieren
   */
  async generateExpose(propertyId, unitId, listingType = 'rent') {
    try {
      // 1. Daten sammeln
      const data = await this.collectData(propertyId, unitId);
      
      // 2. Intelligente Preisberechnung
      const pricing = await this.calculateOptimalPricing(data, listingType);
      
      // 3. KI-generierte Beschreibung
      const description = await this.generateDescription(data, pricing);
      
      // 4. Exposé-Struktur zusammenstellen
      const expose = {
        property: data.property,
        unit: data.unit,
        tenant: data.tenant,
        pricing: pricing,
        description: description,
        features: this.extractFeatures(data),
        marketAnalysis: pricing.marketAnalysis,
        recommendations: this.generateRecommendations(data, pricing),
        generatedAt: new Date().toISOString()
      };

      return expose;
    } catch (error) {
      console.error('Error generating expose:', error);
      throw error;
    }
  }

  /**
   * Datensammlung aus Supabase
   */
  async collectData(propertyId, unitId) {
    // Property-Daten
    const { data: property, error: propertyError } = await masterSupabase
      .from('properties')
      .select('*')
      .eq('id', propertyId)
      .single();

    if (propertyError) throw propertyError;

    // Unit-Daten
    const { data: unit, error: unitError } = await masterSupabase
      .from('units')
      .select('*')
      .eq('id', unitId)
      .single();

    if (unitError) throw unitError;

    // Tenant-Daten (falls vorhanden)
    const { data: tenants } = await masterSupabase
      .from('tenants')
      .select('*')
      .eq('unit_id', unitId)
      .order('created_at', { ascending: false })
      .limit(1);

    const tenant = tenants && tenants.length > 0 ? tenants[0] : null;

    return { property, unit, tenant };
  }

  /**
   * Intelligente Preisberechnung mit Mietspiegel-Integration
   */
  async calculateOptimalPricing(data, listingType) {
    const { property, unit, tenant } = data;

    // Basis-Marktdaten ermitteln
    const marketData = await this.getMarketData(property.city, unit.rooms, unit.size_sqm);
    
    // Standort-Faktor
    const locationData = this.locationMultipliers[property.city] || this.locationMultipliers.default;
    const locationFactor = this.determineLocationQuality(property) === 'premium' 
      ? locationData.premium 
      : locationData.base;

    // Ausstattungs-Bonus berechnen
    const equipmentBonus = this.calculateEquipmentBonus(unit);
    
    // Basis-Mietpreis berechnen
    const baseRentPerSqm = marketData.averageRentPerSqm * locationFactor;
    const baseRent = baseRentPerSqm * unit.size_sqm;
    
    // Ausstattungsaufschlag
    const equipmentAdjustment = baseRent * (equipmentBonus.totalFactor - 1);
    
    // Optimaler Mietpreis
    const optimalRent = Math.round(baseRent + equipmentAdjustment);
    
    // Vergleich mit aktueller Miete (falls Tenant vorhanden)
    const currentRent = tenant?.monthly_rent || 0;
    const rentIncreasePotential = optimalRent - currentRent;
    
    // Nebenkosten schätzen
    const estimatedUtilities = this.estimateUtilities(unit, property);
    
    // Kaution berechnen (2-3 Monatsmieten)
    const deposit = optimalRent * 2.5;

    return {
      baseRent: Math.round(baseRent),
      equipmentBonus: Math.round(equipmentAdjustment),
      optimalRent: optimalRent,
      currentRent: currentRent,
      rentIncreasePotential: rentIncreasePotential,
      utilities: estimatedUtilities,
      deposit: Math.round(deposit),
      totalCost: optimalRent + estimatedUtilities.total,
      pricePerSqm: Math.round(optimalRent / unit.size_sqm),
      marketAnalysis: {
        marketAverage: Math.round(marketData.averageRentPerSqm * unit.size_sqm),
        positionVsMarket: optimalRent > (marketData.averageRentPerSqm * unit.size_sqm) ? 'above' : 'below',
        percentageDifference: Math.round(((optimalRent / (marketData.averageRentPerSqm * unit.size_sqm)) - 1) * 100),
        locationFactor: locationFactor,
        equipmentFactors: equipmentBonus.factors
      }
    };
  }

  /**
   * Marktdaten ermitteln (in Produktion: API-Integration)
   */
  async getMarketData(city, rooms, sizeSqm) {
    // Simulierte Marktdaten - in Produktion aus Mietspiegel-API
    const marketRates = {
      'München': { 1: 25, 2: 22, 3: 20, 4: 18, 5: 16 },
      'Frankfurt': { 1: 22, 2: 20, 3: 18, 4: 16, 5: 14 },
      'Hamburg': { 1: 18, 2: 16, 3: 15, 4: 14, 5: 13 },
      'Berlin': { 1: 16, 2: 14, 3: 13, 4: 12, 5: 11 },
      'default': { 1: 12, 2: 11, 3: 10, 4: 9, 5: 8 }
    };

    const cityRates = marketRates[city] || marketRates.default;
    const roomKey = Math.min(rooms, 5);
    const averageRentPerSqm = cityRates[roomKey] || cityRates[3];

    return {
      city,
      rooms,
      sizeSqm,
      averageRentPerSqm,
      dataSource: 'Clara360 Mietspiegel Integration',
      lastUpdated: new Date().toISOString()
    };
  }

  /**
   * Ausstattungsbonus berechnen
   */
  calculateEquipmentBonus(unit) {
    let totalFactor = 1.0;
    const appliedFactors = [];

    // Unit-Features analysieren (angenommen als JSON oder String)
    const features = this.parseUnitFeatures(unit);
    
    features.forEach(feature => {
      const equipmentFactor = this.equipmentFactors[feature.toLowerCase()];
      if (equipmentFactor) {
        totalFactor *= equipmentFactor.factor;
        appliedFactors.push({
          feature: equipmentFactor.description,
          factor: equipmentFactor.factor,
          impact: `+${Math.round((equipmentFactor.factor - 1) * 100)}%`
        });
      }
    });

    return {
      totalFactor,
      factors: appliedFactors,
      description: appliedFactors.map(f => f.feature).join(', ')
    };
  }

  /**
   * Unit-Features aus verschiedenen Feldern extrahieren
   */
  parseUnitFeatures(unit) {
    const features = [];
    
    // Aus verschiedenen Unit-Feldern Features extrahieren
    if (unit.kitchen_type) features.push(unit.kitchen_type);
    if (unit.bathroom_type) features.push(unit.bathroom_type);
    if (unit.flooring_type) features.push(unit.flooring_type);
    if (unit.heating_type) features.push(unit.heating_type);
    if (unit.has_balcony) features.push('balkon');
    if (unit.has_terrace) features.push('terrasse');
    if (unit.has_garden) features.push('garten');
    if (unit.has_elevator) features.push('aufzug');
    if (unit.has_cellar) features.push('keller');
    if (unit.has_parking) features.push('stellplatz');
    if (unit.has_garage) features.push('garage');
    
    // Features aus JSON-Feld (falls vorhanden)
    if (unit.features && typeof unit.features === 'string') {
      try {
        const jsonFeatures = JSON.parse(unit.features);
        features.push(...jsonFeatures);
      } catch (e) {
        // Features als kommagetrennte Liste
        features.push(...unit.features.split(',').map(f => f.trim()));
      }
    }

    return features.filter(f => f && f.length > 0);
  }

  /**
   * Standortqualität bestimmen
   */
  determineLocationQuality(property) {
    // Vereinfachte Logik - in Produktion: Mikrolage-Analyse
    const premiumIndicators = [
      'zentrum', 'innenstadt', 'altstadt', 'mitte',
      'prenzlauer berg', 'charlottenburg', 'schwabing',
      'westend', 'bogenhausen'
    ];

    const address = (property.address + ' ' + property.district + ' ' + property.city).toLowerCase();
    
    return premiumIndicators.some(indicator => address.includes(indicator)) ? 'premium' : 'standard';
  }

  /**
   * Nebenkosten schätzen
   */
  estimateUtilities(unit, property) {
    const baseCostPerSqm = 3.5; // €/m² Grundkosten
    const heatingCostPerSqm = 1.8; // €/m² Heizkosten
    
    const baseUtilities = unit.size_sqm * baseCostPerSqm;
    const heatingCosts = unit.size_sqm * heatingCostPerSqm;
    
    return {
      base: Math.round(baseUtilities),
      heating: Math.round(heatingCosts),
      total: Math.round(baseUtilities + heatingCosts),
      breakdown: {
        'Hausgeld': Math.round(baseUtilities * 0.6),
        'Heizkosten': Math.round(heatingCosts),
        'Wasser/Abwasser': Math.round(baseUtilities * 0.25),
        'Müllgebühren': Math.round(baseUtilities * 0.15)
      }
    };
  }

  /**
   * KI-generierte Objektbeschreibung
   */
  async generateDescription(data, pricing) {
    const { property, unit, tenant } = data;
    
    // Ausstattungs-Highlights
    const features = this.extractFeatures(data);
    const highlights = features.premium.slice(0, 3);
    
    // Lage-Beschreibung
    const locationDesc = this.generateLocationDescription(property);
    
    // Zielgruppen-Ansprache
    const targetGroup = this.determineTargetGroup(unit);
    
    // Hauptbeschreibung generieren
    const title = this.generateTitle(unit, property);
    const mainDescription = this.generateMainDescription(unit, property, highlights, targetGroup);
    const locationDescription = locationDesc;
    const equipmentDescription = this.generateEquipmentDescription(features);
    const priceJustification = this.generatePriceJustification(pricing);

    return {
      title,
      subtitle: `${unit.rooms}-Zimmer-Wohnung, ${unit.size_sqm}m² in ${property.city}`,
      mainDescription,
      locationDescription,
      equipmentDescription,
      priceJustification,
      highlights: highlights,
      targetGroup,
      seoKeywords: this.generateSEOKeywords(unit, property),
      callToAction: this.generateCallToAction(targetGroup)
    };
  }

  /**
   * Titel generieren
   */
  generateTitle(unit, property) {
    const adjectives = ['Charmante', 'Moderne', 'Helle', 'Geräumige', 'Attraktive', 'Komfortable'];
    const randomAdjective = adjectives[Math.floor(Math.random() * adjectives.length)];
    
    return `${randomAdjective} ${unit.rooms}-Zimmer-Wohnung in ${property.city}`;
  }

  /**
   * Hauptbeschreibung generieren
   */
  generateMainDescription(unit, property, highlights, targetGroup) {
    const descriptions = {
      family: `Diese wunderschöne ${unit.rooms}-Zimmer-Wohnung bietet auf ${unit.size_sqm}m² alles, was eine Familie braucht. Die durchdachte Raumaufteilung und die hochwertige Ausstattung schaffen ein behagliches Zuhause für die ganze Familie.`,
      
      single: `Perfekt für Singles oder Paare: Diese moderne ${unit.rooms}-Zimmer-Wohnung überzeugt durch ihre optimale Raumnutzung auf ${unit.size_sqm}m². Hier lässt es sich entspannt leben und arbeiten.`,
      
      student: `Ideale Wohnung für Studenten oder Berufseinsteiger! Die ${unit.rooms}-Zimmer-Wohnung bietet auf ${unit.size_sqm}m² alles Notwendige zu einem fairen Preis. Perfekte Anbindung und gute Infrastruktur inklusive.`,
      
      senior: `Barrierefreies Wohnen in bester Lage: Diese komfortable ${unit.rooms}-Zimmer-Wohnung ist ideal für Senioren geeignet. Auf ${unit.size_sqm}m² finden Sie alles, was Sie für einen entspannten Lebensabend benötigen.`
    };

    let baseDescription = descriptions[targetGroup] || descriptions.single;
    
    if (highlights.length > 0) {
      baseDescription += ` Besondere Highlights sind ${highlights.join(', ')}.`;
    }

    return baseDescription;
  }

  /**
   * Lage-Beschreibung generieren
   */
  generateLocationDescription(property) {
    return `Die Wohnung befindet sich in ${property.city}, ${property.district || 'einer beliebten Wohnlage'}. Die Infrastruktur bietet alles für den täglichen Bedarf: Einkaufsmöglichkeiten, Restaurants und öffentliche Verkehrsmittel sind fußläufig erreichbar.`;
  }

  /**
   * Ausstattungs-Beschreibung generieren
   */
  generateEquipmentDescription(features) {
    const { standard, premium } = features;
    
    let description = 'Die Wohnung verfügt über eine solide Grundausstattung';
    
    if (standard.length > 0) {
      description += ` mit ${standard.join(', ')}`;
    }
    
    if (premium.length > 0) {
      description += `. Besondere Ausstattungsmerkmale sind ${premium.join(', ')}`;
    }
    
    description += '.';
    
    return description;
  }

  /**
   * Preisbegründung generieren
   */
  generatePriceJustification(pricing) {
    const { marketAnalysis } = pricing;
    
    let justification = `Der Mietpreis von €${pricing.optimalRent} entspricht `;
    
    if (marketAnalysis.positionVsMarket === 'above') {
      justification += `dem oberen Marktsegment (+${marketAnalysis.percentageDifference}% über Durchschnitt) und ist durch die hochwertige Ausstattung und die erstklassige Lage gerechtfertigt.`;
    } else {
      justification += `dem aktuellen Marktniveau und bietet ein ausgezeichnetes Preis-Leistungs-Verhältnis.`;
    }
    
    return justification;
  }

  /**
   * Features extrahieren und kategorisieren
   */
  extractFeatures(data) {
    const { unit } = data;
    const allFeatures = this.parseUnitFeatures(unit);
    
    const premiumFeatures = [];
    const standardFeatures = [];
    
    allFeatures.forEach(feature => {
      const equipmentFactor = this.equipmentFactors[feature.toLowerCase()];
      if (equipmentFactor) {
        if (equipmentFactor.factor > 1.05) {
          premiumFeatures.push(equipmentFactor.description);
        } else {
          standardFeatures.push(equipmentFactor.description);
        }
      }
    });

    return {
      premium: premiumFeatures,
      standard: standardFeatures,
      all: allFeatures
    };
  }

  /**
   * Zielgruppe bestimmen
   */
  determineTargetGroup(unit) {
    if (unit.rooms >= 4) return 'family';
    if (unit.rooms <= 2 && unit.size_sqm <= 60) return 'student';
    if (unit.rooms === 2 || unit.rooms === 3) return 'single';
    return 'family';
  }

  /**
   * SEO-Keywords generieren
   */
  generateSEOKeywords(unit, property) {
    return [
      `${unit.rooms} Zimmer ${property.city}`,
      `Wohnung mieten ${property.city}`,
      `${unit.size_sqm}m² ${property.city}`,
      `${property.district} Wohnung`,
      `Mietwohnung ${property.city}`,
      `${unit.rooms}-Zimmer-Wohnung`
    ];
  }

  /**
   * Call-to-Action generieren
   */
  generateCallToAction(targetGroup) {
    const ctas = {
      family: 'Vereinbaren Sie noch heute einen Besichtigungstermin für Ihr neues Familienheim!',
      single: 'Kontaktieren Sie uns für eine Besichtigung - Ihr neues Zuhause wartet auf Sie!',
      student: 'Schnell sein lohnt sich - vereinbaren Sie jetzt einen Besichtigungstermin!',
      senior: 'Lassen Sie sich diese komfortable Wohnung in ruhiger Lage nicht entgehen!'
    };
    
    return ctas[targetGroup] || ctas.single;
  }

  /**
   * Empfehlungen für Optimierung generieren
   */
  generateRecommendations(data, pricing) {
    const recommendations = [];
    
    // Preisempfehlungen
    if (pricing.rentIncreasePotential > 50) {
      recommendations.push({
        type: 'pricing',
        priority: 'high',
        title: 'Mieterhöhungspotenzial',
        description: `Die aktuelle Miete liegt €${pricing.rentIncreasePotential} unter dem Marktpreis. Eine Anpassung wäre rechtlich möglich.`,
        action: 'Mieterhöhung prüfen'
      });
    }
    
    // Ausstattungsempfehlungen
    const features = this.extractFeatures(data);
    if (features.premium.length < 2) {
      recommendations.push({
        type: 'equipment',
        priority: 'medium',
        title: 'Ausstattung verbessern',
        description: 'Durch kleine Investitionen in die Ausstattung könnte der Mietpreis gesteigert werden.',
        action: 'Modernisierung planen'
      });
    }
    
    // Marketing-Empfehlungen
    recommendations.push({
      type: 'marketing',
      priority: 'medium',
      title: 'Multi-Portal Veröffentlichung',
      description: 'Für maximale Reichweite auf allen relevanten Portalen veröffentlichen.',
      action: 'Auf allen Portalen schalten'
    });

    return recommendations;
  }
}

export default ExposePricingEngine;

